import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  LinearProgress,
  Box,
  makeStyles
} from "@material-ui/core";
import Portal from "@material-ui/core/Portal";
import { useTheme, useMediaQuery } from "@material-ui/core";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorIcon from "@material-ui/icons/Error";
import WarningIcon from "@material-ui/icons/Warning";

const useStyles = makeStyles((theme) => ({
  dialogContent: {
    padding: theme.spacing(3),
    minHeight: 150,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center"
  },
  statusContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(2),
    width: "100%"
  },
  statusText: {
    textAlign: "center",
    marginTop: theme.spacing(1)
  },
  progressBar: {
    width: "100%",
    marginTop: theme.spacing(2)
  },
  icon: {
    fontSize: 48
  },
  successIcon: {
    color: theme.palette.success.main
  },
  errorIcon: {
    color: theme.palette.error.main
  },
  warningIcon: {
    color: theme.palette.warning.main
  },
  details: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    fontSize: "0.875rem"
  }
}));

const ProgressModal = ({ 
  open, 
  onClose, 
  title = "Processando...",
  status = "processing", // processing, success, error, warning
  message = "",
  details = null,
  showProgress = true,
  progress = 0,
  onCloseButton = null
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const getStatusIcon = () => {
    switch (status) {
      case "success":
        return <CheckCircleIcon className={`${classes.icon} ${classes.successIcon}`} />;
      case "error":
        return <ErrorIcon className={`${classes.icon} ${classes.errorIcon}`} />;
      case "warning":
        return <WarningIcon className={`${classes.icon} ${classes.warningIcon}`} />;
      default:
        return null;
    }
  };

  const canClose = status === "success" || status === "error" || status === "warning";
  const canMinimize = status === "processing" && onClose; // Permitir minimizar durante processamento

  return (
    <Portal container={document.body}>
      <Dialog
        open={open}
        onClose={canClose ? onClose : undefined}
        aria-labelledby="progress-dialog"
        disableEnforceFocus={true}
        disableAutoFocus={true}
        disableRestoreFocus={true}
        fullWidth={isMobile}
        maxWidth={isMobile ? false : "sm"}
        PaperProps={{
          style: {
            zIndex: 10003,
            position: 'fixed',
            margin: isMobile ? '16px' : '24px',
            maxWidth: isMobile ? 'calc(100% - 32px)' : '500px',
            width: isMobile ? 'calc(100% - 32px)' : 'auto'
          }
        }}
        BackdropProps={{
          style: {
            zIndex: 10002,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            position: 'fixed',
            pointerEvents: canMinimize ? 'auto' : (canClose ? 'auto' : 'none') // Permitir clicar no backdrop para minimizar
          },
          onClick: canMinimize ? onClose : undefined
        }}
        style={{
          zIndex: 10003,
          position: 'fixed'
        }}
      >
        <DialogTitle id="progress-dialog" style={{ padding: isMobile ? '16px' : '24px' }}>
          {title}
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <Box className={classes.statusContainer}>
            {status === "processing" && (
              <>
                <CircularProgress size={48} />
                {showProgress && (
                  <Box className={classes.progressBar}>
                    <LinearProgress 
                      variant={progress > 0 ? "determinate" : "indeterminate"} 
                      value={progress}
                    />
                  </Box>
                )}
              </>
            )}
            
            {status !== "processing" && getStatusIcon()}
            
            <Typography variant="body1" className={classes.statusText}>
              {message || getDefaultMessage()}
            </Typography>
            
            {details && (
              <Box className={classes.details}>
                <Typography variant="body2" component="pre" style={{ 
                  whiteSpace: "pre-wrap", 
                  wordBreak: "break-word",
                  margin: 0,
                  fontFamily: "inherit"
                }}>
                  {details}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        {(canClose || canMinimize) && (
          <DialogActions style={{ padding: isMobile ? '8px 16px' : '16px 24px' }}>
            {canMinimize && (
              <Button
                onClick={() => {
                  console.log('[ProgressModal] Botão Minimizar clicado');
                  if (onClose) {
                    onClose();
                  }
                }}
                variant="outlined"
                color="default"
                style={{
                  pointerEvents: 'auto',
                  touchAction: 'manipulation',
                  minWidth: '120px',
                  minHeight: '36px',
                  marginRight: '8px'
                }}
              >
                Minimizar
              </Button>
            )}
            {canClose && (
              <Button
                onClick={onCloseButton || onClose}
                variant="contained"
                color={status === "error" ? "secondary" : "primary"}
                style={{
                  pointerEvents: 'auto',
                  touchAction: 'manipulation',
                  minWidth: '100px',
                  minHeight: '36px'
                }}
              >
                {status === "error" ? "Fechar" : "OK"}
              </Button>
            )}
          </DialogActions>
        )}
      </Dialog>
    </Portal>
  );

  function getDefaultMessage() {
    switch (status) {
      case "processing":
        return "Aguarde, processando operação...";
      case "success":
        return "Operação realizada com sucesso!";
      case "error":
        return "Erro ao processar operação.";
      case "warning":
        return "Operação concluída com avisos.";
      default:
        return "";
    }
  }
};

export default ProgressModal;

